import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress, 
  FormControl,
  Grid, 
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField, 
  Typography 
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import { db } from "../../../../../Config/firebase.ts";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { Lead } from '../Inflow';
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2.ts';

type EditLeadProps = {
  lead: Lead | null;
};

export default function EditLeadModal({ lead }: EditLeadProps) {
  const open = useAppSelector((state: RootState) => state.ModalSlice.EditLeadModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const userId = useAppSelector(selectEffectiveUID);
  const userEmail = useAppSelector((state: RootState) => state.userSliceV2.data?.email);
  const userDisplayName = useAppSelector((state: RootState) => state.userSliceV2.data?.display_name);
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<Lead>({
    id: '',
    name: '',
    phone: '',
    email: '',
    source: 'Website',
    status: 'hot',
    notes: '',
    lastContact: new Date().toISOString().split('T')[0],
    originatorId: 'current-user'
  });

  useEffect(() => {
    if (lead) {
      setFormData(lead);
    }
  }, [lead]);

  const handleClose = () => {
    dispatch(closeModal("EditLeadModalOpen"));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({...formData, [name]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone number format (10 digits)
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!userId || !lead?.id) {
      toast.error("Missing user or lead information");
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      const timestamp = Date.now();
      const updatedBy = userDisplayName || userEmail || 'Unknown';
      
      // Track changes
      const changes: Record<string, any> = {};
      
      if (lead.name !== formData.name) changes.name = { from: lead.name, to: formData.name };
      if (lead.phone !== formData.phone) changes.phone = { from: lead.phone, to: formData.phone };
      if (lead.email !== formData.email) changes.email = { from: lead.email, to: formData.email };
      if (lead.source !== formData.source) changes.source = { from: lead.source, to: formData.source };
      if (lead.status !== formData.status) changes.status = { from: lead.status, to: formData.status };
      if (lead.lastContact !== formData.lastContact) changes.lastContact = { from: lead.lastContact, to: formData.lastContact };
      if (lead.originatorId !== formData.originatorId) changes.originatorId = { from: lead.originatorId, to: formData.originatorId };
      
      // Create notes history entry if notes changed
      let notesHistoryEntry = null;
      if (lead.notes !== formData.notes) {
        notesHistoryEntry = {
          timestamp,
          note: lead.notes,  // Store the previous note
          updatedBy
        };
      }
      
      // Create update history entry
      const updateHistoryEntry = {
        timestamp,
        updatedBy,
        changes
      };
      
      // Update lead in Firestore
      const leadDocRef = doc(db, `ServiceProviders/${userId}/Leads`, lead.id);
      
      const updateData: any = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        source: formData.source,
        status: formData.status,
        notes: formData.notes,
        lastContact: formData.lastContact,
        originatorId: formData.originatorId,
        updatedAt: timestamp
      };
      
      // Add notes to history if changed
      if (notesHistoryEntry) {
        updateData.notesHistory = arrayUnion(notesHistoryEntry);
      }
      
      // Add to update history if there are changes
      if (Object.keys(changes).length > 0) {
        updateData.updateHistory = arrayUnion(updateHistoryEntry);
      }
      
      await updateDoc(leadDocRef, updateData);
      
      // UI is updated through Firebase listener
      
      toast.success("Lead updated successfully!");
      handleClose();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead. Please try again.");
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  if (!lead) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '10px',
        },
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <Typography variant="h6">Edit Lead</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10-digit number"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel id="lead-source-label">Source</InputLabel>
                <Select
                  labelId="lead-source-label"
                  id="lead-source"
                  name="source"
                  value={formData.source}
                  label="Source"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Email Campaign">Email Campaign</MenuItem>
                  <MenuItem value="Trade Show">Trade Show</MenuItem>
                  <MenuItem value="Cold Call">Cold Call</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel id="lead-status-label">Status</InputLabel>
                <Select
                  labelId="lead-status-label"
                  id="lead-status"
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="hot">Hot</MenuItem>
                  <MenuItem value="cold">Cold</MenuItem>
                  <MenuItem value="obsolete">Obsolete</MenuItem>
                  <MenuItem value="win">Win</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="date"
                label="Last Contact"
                name="lastContact"
                value={formData.lastContact}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={3}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : null}
              >
                {isLoading ? 'Updating...' : 'Update Lead'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
} 